// The public landing page.
//
// Until now a signed-out visitor to buildbotsnow.com was shown a login box,
// which asks somebody to create an account for a product they have not been
// told anything about. This page is the thing that has to do the selling.
//
// Deliberately absent: customer logos, testimonials, and "trusted by N
// businesses". Every competitor's homepage has them and we do not have the
// customers yet. Inventing them is fraud with our postal address printed at
// the bottom, so the page is built to look substantial without them -- the
// product demo does the work a logo strip usually does. See lib/marketing.ts.

import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import HeroCarousel from "../components/HeroCarousel";
import {
  DEMO_AGENT_ID,
  DEMO_SCRIPT_URL,
  FAQS,
  FEATURES,
  HERO_ASSURANCES,
  HERO_HEADLINE,
  HERO_PATH_NOTE,
  HERO_PRIMARY_CTA,
  HERO_SECONDARY_CTA,
  HERO_SUBHEAD,
  HOW_IT_WORKS_CTA,
  PRICING_HEADLINE,
  PRICING_SUBHEAD,
  STEPS,
  HERO_BADGE,
  HOW_IT_WORKS_BODY
} from "../lib/marketing";
import {
  ENTERPRISE,
  FREE_AGENT_LIMIT,
  FREE_ANSWERS_PER_MONTH,
  FREE_HISTORY_DAYS,
  PAID_PLAN_IDS,
  PLANS,
  answers,
  usd, EARLY_ACCESS, earlyAccessActive } from "../lib/plans";
import { PRODUCT_EMAIL, PRODUCT_NAME } from "../lib/brand";
import { askSiteAssistant } from "../components/SiteAssistant";

// What the Enterprise "Talk to us" button asks the house bot on the visitor's
// behalf.
export const ENTERPRISE_QUESTION = "I'd like to talk about an Enterprise plan.";
import { withCompanyLink } from "../components/CompanyLink";
import { CUSTOM_WORK_OPEN, CUSTOM_WORK_VISIBLE } from "../lib/devTime";
import { Logo, LogoMark } from "../components/Logo";
import Footer from "../components/Footer";
import { ChevronIcon } from "../components/ui";
import { ColorModeToggle } from "../components/ColorModeToggle";

// buildbotsnow.com/#pricing, from an ad's sitelink or a pasted link: the
// browser looks for #pricing in the prerendered page, then the app draws the
// page again (main.tsx) and the jump is lost. So it is made again here, and
// once more when images have loaded and moved the section down, unless the
// visitor has scrolled since.
export function useScrollToHash() {
  useEffect(() => {
    const win = window;
    const id = decodeURIComponent(win.location.hash.slice(1));
    if (!id) return;
    const jump = () => win.document.getElementById(id)?.scrollIntoView?.();
    jump();
    if (win.document.readyState === "complete") return;
    let scrolled = false;
    const onWheel = () => (scrolled = true);
    const onLoad = () => {
      if (!scrolled) jump();
    };
    win.addEventListener("wheel", onWheel, { passive: true });
    win.addEventListener("touchmove", onWheel, { passive: true });
    win.addEventListener("keydown", onWheel);
    win.addEventListener("load", onLoad);
    return () => {
      win.removeEventListener("wheel", onWheel);
      win.removeEventListener("touchmove", onWheel);
      win.removeEventListener("keydown", onWheel);
      win.removeEventListener("load", onLoad);
    };
  }, []);
}

export default function Home() {
  useScrollToHash();
  return (
    <div className="flex min-h-screen flex-col bg-surface">
      <MarketingNav />
      <main className="flex-1">
        <Hero />
        <HowItWorks />
        <Features />
        <Pricing />
        <Faq />
        <ClosingCta />
      </main>
      <Footer />
    </div>
  );
}

/* ─────────────────────────── nav ─────────────────────────── */

function MarketingNav() {
  return (
    // Translucent and sticky: the page is long, and the "Start free" button
    // should never be more than a thumb-scroll away.
    <header className="sticky top-0 z-30 border-b border-slate-200/70 bg-surface/80 backdrop-blur">
      <div className="mx-auto flex max-w-6xl items-center gap-3 px-4 py-3.5 sm:gap-6">
        <Link to="/" aria-label={PRODUCT_NAME} className="flex shrink-0">
          {/* The name stays on phones (Luis, October 24, 2026); only the
              narrowest, 320px, need the room. */}
          <Logo compact="tight" />
        </Link>

        <nav className="ml-auto hidden items-center gap-1 md:flex" aria-label="Sections">
          {SECTIONS.map(([href, label]) => (
            <NavLink key={href} href={href}>
              {label}
            </NavLink>
          ))}
          <Link to="/compare" className="rounded-lg px-3 py-2 text-sm font-medium text-slate-600 transition hover:bg-slate-100 hover:text-slate-900">
            Compare
          </Link>
        </nav>

        <div className="ml-auto flex items-center gap-1 sm:gap-2 md:ml-0">
          <span className="hidden md:inline-flex">
            <ColorModeToggle />
          </span>
          <Link
            to="/signin"
            className="hidden whitespace-nowrap rounded-lg px-2 py-2 text-sm font-medium text-slate-600 transition hover:bg-slate-100 sm:px-3 md:inline-block"
          >
            Sign in
          </Link>
          <Link
            to="/start"
            className="whitespace-nowrap rounded-lg bg-slate-900 px-3 py-2 text-sm font-semibold text-surface transition hover:bg-slate-800 sm:px-4"
          >
            {/* The full wording does not fit beside the logo on a phone. */}
            <span className="sm:hidden">Start free</span>
            <span className="hidden sm:inline">{HERO_PRIMARY_CTA}</span>
          </Link>
          <MobileMenu />
        </div>
      </div>
    </header>
  );
}

const SECTIONS: [string, string][] = [
  ["#how", "How it works"],
  ["#features", "Features"],
  ["#pricing", "Pricing"],
  ["#faq", "FAQ"]
];

// Phones and small tablets, where the section links do not fit: a menu with
// the same links, the comparison, sign in and color mode (Luis, October 24,
// 2026: the mobile site had no way to find prices).
function MobileMenu() {
  const [open, setOpen] = useState(false);
  const box = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const close = (event: MouseEvent | KeyboardEvent) => {
      if (event instanceof KeyboardEvent ? event.key === "Escape" : !box.current?.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", close);
    document.addEventListener("keydown", close);
    return () => {
      document.removeEventListener("mousedown", close);
      document.removeEventListener("keydown", close);
    };
  }, [open]);

  const item = "block rounded-lg px-3 py-2.5 text-base font-medium text-slate-700 hover:bg-slate-100";
  return (
    <div ref={box} className="relative md:hidden">
      <button
        type="button"
        aria-label="Menu"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen(!open)}
        className="flex h-10 w-10 items-center justify-center rounded-lg text-slate-700 hover:bg-slate-100"
      >
        <svg viewBox="0 0 24 24" className="h-6 w-6" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
          {open ? <path d="M6 6l12 12M18 6L6 18" /> : <path d="M4 7h16M4 12h16M4 17h16" />}
        </svg>
      </button>
      {open && (
        <div role="menu" aria-label="Site menu" className="absolute right-0 z-40 mt-2 w-64 rounded-xl border border-slate-200 bg-surface p-2 shadow-lg">
          {SECTIONS.map(([href, label]) => (
            <a key={href} role="menuitem" href={href} onClick={() => setOpen(false)} className={item}>
              {label}
            </a>
          ))}
          <Link role="menuitem" to="/compare" onClick={() => setOpen(false)} className={item}>
            Compare
          </Link>
          <div className="my-1 border-t border-slate-100" />
          <Link role="menuitem" to="/signin" onClick={() => setOpen(false)} className={item}>
            Sign in
          </Link>
          <div className="flex items-center justify-between px-3 py-1.5">
            <span className="text-base font-medium text-slate-700">Dark mode</span>
            <ColorModeToggle />
          </div>
        </div>
      )}
    </div>
  );
}

function NavLink({ href, children }: { href: string; children: React.ReactNode }) {
  return (
    <a
      href={href}
      className="rounded-lg px-3 py-2 text-sm font-medium text-slate-600 transition hover:bg-slate-100 hover:text-slate-900"
    >
      {children}
    </a>
  );
}

/* ─────────────────────────── hero ─────────────────────────── */

function Hero() {
  return (
    <section className="relative overflow-hidden">
      <GradientWash />

      <div className="relative mx-auto grid max-w-6xl items-center gap-8 px-4 pb-16 pt-8 sm:gap-14 sm:pb-20 sm:pt-16 lg:grid-cols-2 lg:pb-28 lg:pt-24">
        {/* min-w-0 on both columns. A grid item's automatic minimum size is its
            min-content width, and the install snippet below does not wrap -- so
            without this the column sizes itself to the length of a URL and the
            whole hero, headline included, runs off the side of a phone. The
            section's overflow-hidden turns that into silent clipping rather
            than a scrollbar, which is why it only shows up at narrow widths. */}
        <div className="min-w-0">
          {/* Not on phones: the first screen should reach the picture. */}
          <span className="hidden items-center gap-2 rounded-full border border-brand-200 bg-brand-50 px-3 py-1 text-xs font-medium text-brand-700 sm:inline-flex">
            <SparkIcon className="h-3.5 w-3.5" />
            {HERO_BADGE}
          </span>

          <h1 className="text-[2.1rem] font-bold leading-tight tracking-tight text-slate-900 sm:mt-5 sm:text-5xl lg:text-[3.4rem] lg:leading-[1.05]">
            {HERO_HEADLINE}
          </h1>

          <p className="mt-4 max-w-xl text-base leading-relaxed text-slate-600 sm:mt-5 sm:text-lg">{HERO_SUBHEAD}</p>

          {/* Two buttons of equal weight, not a button and an afterthought.
              The whole point is that neither route is the "real" one: build
              first if you want to see it work, sign up first if you would
              rather know whose form you are filling in. */}
          <div className="mt-6 flex flex-wrap items-center gap-3 sm:mt-8">
            <Link
              to="/start"
              className="inline-flex items-center gap-2 rounded-xl bg-brand-600 px-6 py-3 text-base font-semibold text-white shadow-lg shadow-brand-600/25 transition hover:bg-brand-700 hover:shadow-brand-700/30"
            >
              {HERO_PRIMARY_CTA}
              <ArrowIcon className="h-4 w-4" />
            </Link>
            <Link
              to="/signin?mode=signup"
              className="inline-flex items-center gap-2 rounded-xl border border-slate-300 bg-surface px-6 py-3 text-base font-semibold text-slate-700 transition hover:bg-slate-50"
            >
              {HERO_SECONDARY_CTA}
            </Link>
          </div>

          {/* Below md the header has no Sign in, only the menu: a returning
              customer should not have to hunt for it. */}
          <p className="mt-4 text-sm text-slate-600 md:hidden">
            Already have an account?{" "}
            <Link to="/signin" className="font-semibold text-brand-700 hover:underline">
              Sign in
            </Link>
          </p>

          <p className="mt-3 max-w-lg text-sm text-slate-500 md:mt-4">
            {HERO_PATH_NOTE}{" "}
            <a href="#how" className="font-medium text-brand-700 hover:underline">
              {HOW_IT_WORKS_CTA}
            </a>
            .
          </p>

          <ul className="mt-7 hidden flex-wrap gap-x-6 gap-y-2 sm:flex">
            {HERO_ASSURANCES.map((item) => (
              <li key={item} className="flex items-center gap-2 text-sm text-slate-500">
                <CheckIcon className="h-4 w-4 text-emerald-500" />
                {item}
              </li>
            ))}
          </ul>
        </div>

        <HeroCarousel />
      </div>
    </section>
  );
}

// The soft color behind the hero. Pure CSS rather than an image so it costs
// nothing to load and stays sharp on any display.
function GradientWash() {
  return (
    // Not -z-10: that puts it behind the page's white background rather than
    // behind the hero's text, and the whole wash disappears.
    <div aria-hidden="true" className="pointer-events-none absolute inset-0">
      <div className="absolute -left-32 -top-32 h-[30rem] w-[30rem] rounded-full bg-brand-200/40 blur-3xl" />
      <div className="absolute -right-24 top-10 h-[26rem] w-[26rem] rounded-full bg-violet-200/40 blur-3xl" />
      <div className="absolute inset-x-0 bottom-0 h-40 bg-gradient-to-b from-transparent to-surface" />
    </div>
  );
}

// A still of the widget, drawn in markup.
//
// This is the product shot the page needs, and drawing it beats screenshotting
// it: a screenshot goes stale the moment the widget's styling changes, renders
// blurry on a retina display, and cannot be read by a screen reader. The
// conversation shown is a plausible one for a business evaluating us.
function ChatDemo() {
  return (
    <div className="relative min-w-0">
      {/* Lifted off the page and tilted very slightly -- enough to read as an
          object sitting on the site rather than a flat block of the layout. */}
      <div className="rotate-[0.6deg] rounded-2xl border border-slate-200 bg-surface p-4 shadow-2xl shadow-slate-900/10">
        <div className="flex items-center gap-3 border-b border-slate-100 pb-3">
          <LogoMark className="h-9 w-9" />
          <div>
            <div className="text-sm font-semibold text-slate-900">Northwind Supply</div>
            <div className="flex items-center gap-1.5 text-xs text-slate-500">
              <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
              Usually replies instantly
            </div>
          </div>
        </div>

        <div className="space-y-3 py-4">
          <Bubble from="visitor">Do you ship to Canada?</Bubble>
          <Bubble from="bot">
            We do! Canadian orders usually arrive in 4 to 6 business days, and shipping is free
            over $75. Duties are collected at checkout so there is nothing to pay on delivery.
          </Bubble>
          <Bubble from="visitor">Can I talk to someone about a bulk order?</Bubble>
          <Bubble from="bot">
            Of course. I have Thursday at 10:00 or Friday at 2:30 open with our sales team.
            Which suits you better?
          </Bubble>
        </div>

        {/* Suggested questions, as the widget draws them (agent.suggestions),
            kept showing here so the drawing has them beside a conversation. */}
        <div className="mb-3 flex flex-wrap gap-1.5" aria-label="Suggested questions" data-testid="demo-suggestions">
          {["What's your return policy?", "Do you have a showroom?", "Where's my order?"].map((question) => (
            <span key={question} className="rounded-full border border-brand-600 px-3 py-1 text-xs text-brand-700">
              {question}
            </span>
          ))}
        </div>

        <div className="flex items-center gap-2 rounded-xl bg-slate-100 px-3 py-2.5">
          <span className="flex-1 text-sm text-slate-400">Ask a question…</span>
          <span className="flex h-7 w-7 items-center justify-center rounded-lg bg-brand-600">
            <ArrowIcon className="h-3.5 w-3.5 text-white" />
          </span>
        </div>
      </div>

      {/* The drawing above is a still. The real thing is running in the
          corner of this page (components/SiteAssistant), which is the better
          argument, so it says so. */}
      <p className="mt-4 text-center text-sm text-slate-500">
        That's a drawing. The real one is in the corner of this page, so ask it anything.
      </p>

      {/* The line the customer actually pastes. Showing it is the argument:
          the whole installation fits on one line.

          Colored span by span rather than run through a highlighter, but the
          characters are exactly what embedSnippet() produces -- Home.test.tsx
          compares the rendered text against it, so this cannot quietly drift
          into a snippet that 404s for anyone who copies it. */}
      <div className="theme-light mt-5 rounded-xl bg-slate-900 px-4 py-3 shadow-xl dark:bg-slate-800 dark:ring-1 dark:ring-inset dark:ring-slate-600">
        {/* whitespace-pre-wrap rather than nowrap: the line is wider than this
            card at every viewport, and nowrap either clipped it mid-attribute
            or hid the end behind a scrollbar nobody would think to drag. Wrapping
            happens at the space before data-agent, so the text a visitor copies
            is byte-for-byte what embedSnippet() produces either way. */}
        <code className="block whitespace-pre-wrap font-mono text-[11px] leading-5 text-slate-300">
          {/* Each half is nowrap so the only break point is the space between
              them. Left to itself the browser breaks at the hyphen in
              data-agent, which reads like a typo rather than a wrapped line.
              On a phone the first half alone is wider than the card, so there
              the address may also break inside itself; nowrap would push the
              whole page sideways. */}
          <span className="sm:whitespace-nowrap">
            <span className="text-slate-500">&lt;</span>
            <span className="text-rose-300">script</span>{" "}
            <span className="text-amber-200">src</span>
            <span className="text-slate-500">=</span>
            <span className="break-all text-emerald-300 sm:break-normal">"{DEMO_SCRIPT_URL}"</span>
          </span>{" "}
          <span className="whitespace-nowrap">
            <span className="text-amber-200">data-agent</span>
            <span className="text-slate-500">=</span>
            <span className="text-emerald-300">"{DEMO_AGENT_ID}"</span>
            <span className="text-slate-500">&gt;&lt;/</span>
            <span className="text-rose-300">script</span>
            <span className="text-slate-500">&gt;</span>
          </span>
        </code>
      </div>
    </div>
  );
}

function Bubble({ from, children }: { from: "bot" | "visitor"; children: React.ReactNode }) {
  const visitor = from === "visitor";
  return (
    <div className={visitor ? "flex justify-end" : "flex justify-start"}>
      <div
        className={
          "max-w-[85%] rounded-2xl px-3.5 py-2.5 text-sm leading-relaxed " +
          (visitor
            ? "rounded-br-md bg-brand-600 text-white"
            : "rounded-bl-md bg-slate-100 text-slate-700")
        }
      >
        {children}
      </div>
    </div>
  );
}

/* ───────────────────────── how it works ───────────────────────── */

function HowItWorks() {
  return (
    <section id="how" className="scroll-mt-20 border-t border-slate-100 bg-surface py-20 lg:py-24">
      <div className="mx-auto max-w-6xl px-4">
        <SectionHeading
          eyebrow="How it works"
          title="Three steps, and none of them are “call us”."
          body={HOW_IT_WORKS_BODY}
        />

        <ol className="mt-14 grid gap-8 md:grid-cols-3">
          {STEPS.map((step, index) => (
            <li key={step.title} className="relative">
              <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-brand-600 text-base font-bold text-white shadow-lg shadow-brand-600/25">
                {index + 1}
              </div>
              <h3 className="mt-5 text-lg font-semibold text-slate-900">{step.title}</h3>
              <p className="mt-2 leading-relaxed text-slate-600">{step.body}</p>
            </li>
          ))}
        </ol>

        {/* What the result looks like, and the one line that installs it.
            It sat in the hero until the carousel took that place; here it is
            the answer to "and then what?" right after the three steps. */}
        <div className="mx-auto mt-16 max-w-xl">
          <ChatDemo />
        </div>
      </div>
    </section>
  );
}

/* ─────────────────────────── features ─────────────────────────── */

function Features() {
  return (
    <section id="features" className="scroll-mt-20 bg-slate-50 py-20 lg:py-24">
      <div className="mx-auto max-w-6xl px-4">
        <SectionHeading
          eyebrow="What you get"
          title="Everything the assistant needs to be useful."
          body="Not a demo you outgrow in a week: the parts a real business actually needs, wherever it meets its customers."
        />

        <div className="mt-14 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {FEATURES.map((feature, index) => (
            <div
              key={feature.title}
              className={
                "rounded-2xl border border-slate-200 bg-surface p-6 shadow-sm transition hover:-translate-y-0.5 hover:shadow-md" +
                // A last card alone on its row sits in the middle, not under
                // the first column.
                (index === FEATURES.length - 1 && FEATURES.length % 3 === 1 ? " lg:col-start-2" : "") +
                (index === FEATURES.length - 1 && FEATURES.length % 2 === 1 ? " md:max-lg:col-span-2 md:max-lg:mx-auto md:max-lg:max-w-md" : "")
              }
            >
              <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-brand-50 text-brand-600">
                <CheckIcon className="h-5 w-5" />
              </div>
              <h3 className="mt-4 font-semibold text-slate-900">{feature.title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-slate-600">{feature.body}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ─────────────────────────── pricing ─────────────────────────── */

function Pricing() {
  return (
    <section id="pricing" className="scroll-mt-20 bg-surface py-20 lg:py-24">
      <div className="mx-auto max-w-6xl px-4">
        <SectionHeading eyebrow="Pricing" title={PRICING_HEADLINE} body={PRICING_SUBHEAD} />

        <div className="mx-auto mt-14 grid max-w-6xl gap-5 md:grid-cols-2 lg:grid-cols-4">
          <div className="flex flex-col rounded-2xl border border-slate-200 bg-surface p-6">
            <h3 className="font-semibold text-slate-900">Free</h3>
            <p className="mt-3 flex items-baseline gap-1">
              <span className="text-4xl font-bold tracking-tight text-slate-900">$0</span>
            </p>
            {/* What it includes first (Luis, October 31, 2026): after the free
                plan narrowed on October 26, a card that led with the limit
                read less generous than it is. The limits stay stated, after. */}
            <p className="mt-3 text-sm text-slate-600" data-testid="free-lead">
              {FREE_ANSWERS_PER_MONTH} answers a month from any number of visitors, free for as long as you like. No card and no trial clock.
            </p>
            <ul className="mt-6 flex-1 space-y-2.5">
              <Perk>{FREE_AGENT_LIMIT === 1 ? "One assistant" : `Up to ${FREE_AGENT_LIMIT} assistants`}, live on your site</Perk>
              <Perk>Its own page and QR code</Perk>
              <Perk>Your content, your colors</Perk>
              <Perk>Hands visitors to you by email</Perk>
              <Perk>Leads to your inbox</Perk>
            </ul>
            <p className="mt-5 text-xs text-slate-500" data-testid="free-paid-note">
              Booking, Slack, Discord, live chat and history beyond {FREE_HISTORY_DAYS} days come with a paid plan.
            </p>
            <Link
              to="/start"
              className="mt-7 block rounded-xl border border-slate-300 px-4 py-2.5 text-center text-sm font-semibold text-slate-700 transition hover:bg-slate-50"
            >
              Start free
            </Link>
          </div>

          {PAID_PLAN_IDS.map((id) => {
            const plan = PLANS[id];
            const featured = id === "starter";
            return (
              <div
                key={id}
                className={
                  featured
                    ? "relative flex flex-col rounded-2xl border-2 border-brand-600 bg-surface p-6 shadow-xl shadow-brand-600/10"
                    : "flex flex-col rounded-2xl border border-slate-200 bg-surface p-6"
                }
              >
                {featured && (
                  <span className="absolute -top-3 left-6 rounded-full bg-brand-600 px-3 py-1 text-xs font-semibold text-white">
                    Recommended
                  </span>
                )}
                <h3 className="font-semibold text-slate-900">{plan.name}</h3>
                <p className="mt-3 flex items-baseline gap-1">
                  <span className="text-4xl font-bold tracking-tight text-slate-900">{usd(plan.priceUsd)}</span>
                  <span className="text-sm text-slate-500">/ month per assistant</span>
                </p>
                {id === EARLY_ACCESS.plan && earlyAccessActive() && (
                  <p className="mt-2 rounded-lg bg-brand-50 px-3 py-2 text-xs text-brand-800" data-testid="early-access">
                    Early access price. Subscribe before {EARLY_ACCESS.untilLabel} and keep {usd(EARLY_ACCESS.priceUsd)} for as
                    long as you stay on {plan.name}. After that it is {usd(EARLY_ACCESS.laterPriceUsd)}.
                  </p>
                )}
                <p className="mt-3 text-sm text-slate-600">
                  {answers(plan.includedAnswers)} answers a month included, then {usd(plan.overagePer1000Usd)} per
                  1,000 extra, up to a spending limit you set.
                </p>
                <ul className="mt-6 flex-1 space-y-2.5">
                  <Perk>Everything in Free</Perk>
                  <Perk>You set the question limit</Perk>
                  <Perk>Appointment booking</Perk>
                  <Perk>Slack, Discord and live chat</Perk>
                  <Perk>All your conversation history</Perk>
                  <Perk>Connect your own API</Perk>
                  <Perk>Change plan or cancel anytime</Perk>
                </ul>
                {/* /start, not a Stripe link: billing is per assistant, so
                    there is nothing to buy until one exists. You build it,
                    then choose its plan. */}
                <Link
                  to="/start"
                  className={
                    featured
                      ? "mt-7 block rounded-xl bg-brand-600 px-4 py-2.5 text-center text-sm font-semibold text-white shadow-lg shadow-brand-600/25 transition hover:bg-brand-700"
                      : "mt-7 block rounded-xl border border-slate-300 px-4 py-2.5 text-center text-sm font-semibold text-slate-700 transition hover:bg-slate-50"
                  }
                >
                  Get started
                </Link>
              </div>
            );
          })}
        </div>

        <div
          className="mx-auto mt-6 flex max-w-6xl flex-col items-start justify-between gap-4 rounded-2xl border border-slate-200 bg-surface p-6 sm:flex-row sm:items-center"
          data-testid="enterprise"
        >
          <div>
            <h3 className="font-semibold text-slate-900">{ENTERPRISE.name}</h3>
            <p className="mt-1 text-sm text-slate-600">
              From {usd(ENTERPRISE.fromUsd)} a month, for sites with millions of visitors: volume pricing
              on answers, an agreed monthly invoice, and help getting set up.
            </p>
          </div>
          {/* Opens our own bot with the question asked, which can bring in a
              person straight away or take the visitor's details for us. The
              email link is the fallback: where the bot does not run, before
              JavaScript, or for whoever prefers email. */}
          <div className="flex shrink-0 flex-col items-center gap-1.5">
            <a
              href={`mailto:${PRODUCT_EMAIL}?subject=${encodeURIComponent("Enterprise plan")}`}
              onClick={(event) => {
                if (askSiteAssistant(ENTERPRISE_QUESTION)) event.preventDefault();
              }}
              className="rounded-xl border border-slate-300 px-4 py-2.5 text-sm font-semibold text-slate-700 transition hover:bg-slate-50"
            >
              Talk to us
            </a>
            <a href={`mailto:${PRODUCT_EMAIL}?subject=${encodeURIComponent("Enterprise plan")}`} className="text-xs text-slate-500 underline">
              or email {PRODUCT_EMAIL}
            </a>
          </div>
        </div>

        {CUSTOM_WORK_VISIBLE && (
        <p className="mx-auto mt-8 max-w-2xl text-center text-sm text-slate-500">
          {CUSTOM_WORK_OPEN ? (
            <>
              Need it wired into something specific, like your CRM, your ticketing system or your
              inventory? We quote that as development work.{" "}
              <Link to="/signin?mode=signup" className="font-medium text-brand-700 hover:underline">
                Get in touch
              </Link>
              .
            </>
          ) : (
            <>
              Need it wired into something specific, like your CRM, your ticketing system or
              your inventory? We are not taking custom integration work yet. When we do, it
              will be quoted as development work.
            </>
          )}
        </p>
        )}
      </div>
    </section>
  );
}

function Perk({ children }: { children: React.ReactNode }) {
  return (
    <li className="flex items-start gap-2.5 text-sm text-slate-600">
      <CheckIcon className="mt-0.5 h-4 w-4 shrink-0 text-emerald-500" />
      {children}
    </li>
  );
}

/* ─────────────────────────── faq ─────────────────────────── */

function Faq() {
  return (
    <section id="faq" className="scroll-mt-20 bg-slate-50 py-20 lg:py-24">
      <div className="mx-auto max-w-3xl px-4">
        <SectionHeading eyebrow="Questions" title="Before you sign up." />

        <dl className="mt-12 space-y-4">
          {FAQS.map((faq) => (
            // <details> rather than a JS accordion: it is open-able before the
            // bundle loads, it is keyboard operable for free, and Ctrl-F finds
            // text inside a closed one in most browsers.
            <details
              key={faq.q}
              className="group rounded-2xl border border-slate-200 bg-surface px-5 open:shadow-sm"
            >
              <summary className="flex cursor-pointer list-none items-center justify-between gap-4 py-4 font-medium text-slate-900 marker:hidden">
                <dt>{faq.q}</dt>
                <ChevronIcon className="h-5 w-5 shrink-0 text-slate-400 transition group-open:rotate-180" />
              </summary>
              <dd className="pb-5 text-sm leading-relaxed text-slate-600">{withCompanyLink(faq.a)}</dd>
            </details>
          ))}
        </dl>
      </div>
    </section>
  );
}

/* ─────────────────────────── closing cta ─────────────────────────── */

function ClosingCta() {
  return (
    <section className="bg-surface px-4 py-20 lg:py-24">
      <div className="theme-light relative mx-auto max-w-5xl overflow-hidden rounded-3xl bg-gradient-to-br from-brand-600 via-brand-600 to-violet-600 px-6 py-16 text-center shadow-2xl shadow-brand-600/20">
        <div
          aria-hidden="true"
          className="pointer-events-none absolute -right-20 -top-24 h-72 w-72 rounded-full bg-white/10 blur-2xl"
        />
        <h2 className="relative text-3xl font-bold tracking-tight text-white sm:text-4xl">
          Stop answering the same question.
        </h2>
        <p className="relative mx-auto mt-4 max-w-xl text-lg text-brand-100">
          Build your assistant now. It is free to try, and you will know within an hour
          whether it is worth keeping.
        </p>
        <Link
          to="/start"
          className="relative mt-8 inline-flex items-center gap-2 rounded-xl bg-white px-7 py-3.5 text-base font-semibold text-brand-700 shadow-lg transition hover:bg-brand-50"
        >
          {HERO_PRIMARY_CTA}
          <ArrowIcon className="h-4 w-4" />
        </Link>

        <p className="relative mt-4 text-sm text-brand-100">
          Or{" "}
          <Link to="/signin?mode=signup" className="font-medium text-white underline">
            create an account first
          </Link>
          .
        </p>
      </div>
    </section>
  );
}

/* ─────────────────────────── shared bits ─────────────────────────── */

function SectionHeading({ eyebrow, title, body }: { eyebrow: string; title: string; body?: string }) {
  return (
    <div className="mx-auto max-w-3xl text-center">
      <span className="text-sm font-semibold uppercase tracking-wider text-brand-600">{eyebrow}</span>
      <h2 className="mt-3 text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">{title}</h2>
      {body && <p className="mx-auto mt-4 max-w-2xl text-lg leading-relaxed text-slate-600">{body}</p>}
    </div>
  );
}

function CheckIcon({ className = "h-4 w-4" }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M16.7 5.3a1 1 0 0 1 0 1.4l-7.5 7.5a1 1 0 0 1-1.4 0l-3.5-3.5a1 1 0 1 1 1.4-1.4l2.8 2.79 6.8-6.79a1 1 0 0 1 1.4 0Z"
      />
    </svg>
  );
}

function ArrowIcon({ className = "h-4 w-4" }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M3 10a1 1 0 0 1 1-1h9.59l-3.3-3.29a1 1 0 1 1 1.42-1.42l5 5a1 1 0 0 1 0 1.42l-5 5a1 1 0 0 1-1.42-1.42l3.3-3.29H4a1 1 0 0 1-1-1Z"
      />
    </svg>
  );
}

function SparkIcon({ className = "h-4 w-4" }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
      <path d="M11 1.5a.6.6 0 0 0-1.1-.3l-5.6 8a.6.6 0 0 0 .5.95h3.4l-.7 7.35a.6.6 0 0 0 1.1.36l5.6-8a.6.6 0 0 0-.5-.95h-3.4l.7-7.41Z" />
    </svg>
  );
}
